import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, FieldRow, FormFooter as UIFormFooter, Input, Title } from '@xsolla/ui-core';
import { AlertDangerColorful, AlertExclamationColorful } from '@xsolla/ui-icons';
import { Modal, ModalAlert } from '@xsolla/ui-modal';

import { getConnectedLaunchers, getConnectedLaunchersNew } from '../../../api';
import {
  ConnectedLaunchers,
  ConnectedLaunchersNew,
  OldLauncherStatus
} from '../../../api/dasboard/types';
import { FormFooter, Preloader } from '../../ui';
import ConnectedLaunchersForm from './ConnectedLaunchersForm';
import { DeleteFormContent, IconWrapper, InputTitle, InputWrapper, TitleHeader } from './style';

interface Props {
  onClose: () => void;
  onDelete: () => void;
  projectName: string;
  merchantId: number;
  projectId: string | undefined;
  deleteFetching: boolean;
  errorDelete?: boolean;
}

const DeleteLoginForm = ({
  projectName,
  onClose,
  onDelete,
  deleteFetching,
  merchantId,
  projectId,
  errorDelete
}: Props): JSX.Element => {
  const intl = useIntl();
  const [fetching, setFetching] = useState<boolean>(true);
  const [errorLauncher, setErrorLauncher] = useState<boolean>(false);
  const [connectedLaunchers, setConnectedLaunchers] = useState<ConnectedLaunchers>([]);
  const [connectedLaunchersNew, setConnectedLaunchersNew] = useState<ConnectedLaunchersNew>([]);
  const [errorInput, setErrorInput] = useState('');
  const [valueInput, setValueInput] = useState('');

  useEffect(() => {
    if (projectId) {
      setErrorLauncher(false);
      Promise.all([
        getConnectedLaunchers({ merchantId, loginProjectId: projectId }),
        getConnectedLaunchersNew({ merchantId, loginProjectId: projectId })
      ])
        .then(([oldConnected, newConnected]) => {
          setConnectedLaunchers(
            oldConnected.filter(
              (oldLauncher) => oldLauncher.status !== OldLauncherStatus.configuration
            )
          );
          setConnectedLaunchersNew(newConnected);
        })
        .catch(() => {
          setErrorLauncher(true);
        })
        .finally(() => {
          setFetching(false);
        });
    }
  }, [projectId]);

  const handleChangeInput = useCallback((el) => {
    setErrorInput('');
    setValueInput(el.target.value);
  }, []);

  const handleOnDelete = useCallback(() => {
    setErrorInput('');
    if (valueInput === projectName) {
      onDelete();
    } else {
      setErrorInput(intl.formatMessage({ id: 'app.validation.not-project-name' }));
    }
  }, [valueInput, errorInput]);

  if (fetching) {
    return (
      <Modal>
        <Preloader />
      </Modal>
    );
  }

  if (errorLauncher) {
    return (
      <ModalAlert
        footer={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <UIFormFooter>
            <FieldRow justify="center" stretched>
              <Button look="tertiary" palette="muted" onClick={onClose}>
                {intl.formatMessage({ id: 'button.cancel' })}
              </Button>
            </FieldRow>
          </UIFormFooter>
        }>
        <>
          <AlertExclamationColorful size={40} />
          <Title center indent="sm">
            {intl.formatMessage({ id: 'app.error.page.label' })}
          </Title>
          <p>
            <FormattedMessage
              id="app.error.page.text"
              values={{
                br: () => <br />
              }}
            />
          </p>
        </>
      </ModalAlert>
    );
  }

  if (connectedLaunchers.length !== 0 || connectedLaunchersNew.length !== 0) {
    return (
      <ConnectedLaunchersForm
        projectName={projectName}
        connectedLaunchers={connectedLaunchers}
        connectedLaunchersNew={connectedLaunchersNew}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal>
      <DeleteFormContent>
        <IconWrapper>
          <AlertDangerColorful size={40} />
        </IconWrapper>

        <TitleHeader indent="sm" data-test-id="delete-modal-header">
          {intl.formatMessage({ id: 'delete-project-form.title' }, { projectName })}
        </TitleHeader>

        <p>{intl.formatMessage({ id: 'delete-project-form.description' })}</p>

        <InputWrapper>
          <InputTitle>{intl.formatMessage({ id: 'delete-project-form.input.title' })}</InputTitle>

          <Input
            data-test-id="input-delete-project"
            errorMessage={errorInput}
            extraAlign="left"
            uiSize="md"
            onChange={handleChangeInput}
          />
        </InputWrapper>
      </DeleteFormContent>

      <FormFooter
        onSubmit={handleOnDelete}
        onCancel={onClose}
        fetching={deleteFetching}
        error={errorDelete}
        isModal
        submitButtonPalette="danger"
        submitText={intl.formatMessage({ id: 'button.delete' })}
      />
    </Modal>
  );
};

export default DeleteLoginForm;
